import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ModalProps } from '../../../../../../components/modal/modal';
import { validate } from '../../../../../../helpers/validate';
import { useLang } from '../../../../../../hooks/use-lang';
import { UpdateExternalReviewRequest, fetchAvailableResources } from '../../../../../../store/api/external-reviews-api';
import { useAlertStore } from '../../../../../../store/use-alert-store';
import { useExternalReviewsStore } from '../../../../../../store/use-external-reviews-store';
import { getSourceIconByName, getSourceInfo, normalizeSourceName } from './helpers';

export const useEditExternalReviewModal = (props: ModalProps) => {
  const { t } = useLang();
  const { externalReviews, updateExternalReview, isLoadingAction } = useExternalReviewsStore();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('reviewId');

  const review = externalReviews?.find((item) => String(item.id) === reviewId);

  const [formData, setFormData] = useState<Partial<UpdateExternalReviewRequest & { profile_url: string }>>({
    config_id: review?.id ? Number(review.id) : 0,
    resource_id: 0,
    profile_url: review?.source_url || '',
    repeat_per_day: review?.collection_frequency || 1,
  });

  const [availableResources, setAvailableResources] = useState<
    { id: number; name: string; type?: string }[]
  >([]);
  const [detectedSource, setDetectedSource] = useState<{ name: string; icon: string } | null>(
    review?.source || null,
  );

  useEffect(() => {
    fetchAvailableResources().then(setAvailableResources).catch(console.error);
  }, []);

  useEffect(() => {
    if (review) {
      const resourceByType = availableResources.find((item) => item.id === review.type);
      const autoSource = getSourceInfo(review.source_url || review.url || '');
      setFormData({
        config_id: Number(review.id),
        resource_id: resourceByType?.id || 0,
        profile_url: review.source_url || review.url || '',
        repeat_per_day: review.collection_frequency || 1,
      });
      if (autoSource) {
        setDetectedSource(autoSource);
      } else if (resourceByType) {
        setDetectedSource({
          name: resourceByType.name,
          icon: getSourceIconByName(resourceByType.name),
        });
      } else {
        setDetectedSource(null);
      }
    }
  }, [review, availableResources]);

  const handleUrlChange = (url: string) => {
    setFormData((prev) => ({ ...prev, profile_url: url }));

    if (url) {
      const sourceInfo = getSourceInfo(url);
      if (sourceInfo) {
        setDetectedSource(sourceInfo);
        const resource = availableResources.find(
          (item) => normalizeSourceName(item.name) === normalizeSourceName(sourceInfo.name),
        );
        setFormData((prev) => ({ ...prev, resource_id: resource?.id || 0 }));
      } else {
        setDetectedSource(null);
        setFormData((prev) => ({ ...prev, resource_id: 0 }));
      }
    } else {
      setDetectedSource(null);
      setFormData((prev) => ({ ...prev, resource_id: 0 }));
    }
  };

  const handleSubmit = async () => {
    if (!reviewId) return;

    const isValid = validate({
      profile_url: { rule: () => !!formData.profile_url && formData.profile_url.length > 0 },
      resource_id: { rule: () => !!formData.resource_id && formData.resource_id > 0 },
      repeat_per_day: { rule: () => !!formData.repeat_per_day && formData.repeat_per_day > 0 },
      // place_id опционален, поэтому не валидируем
    });

    if (!formData.resource_id || formData.resource_id <= 0) {
      useAlertStore
        .getState()
        .add({ label: 'Ресурс не определен по ссылке', variant: 'danger' });
      return;
    }
    if (!isValid) return;

    try {
      await updateExternalReview({
        config_id: Number(reviewId),
        resource_id: formData.resource_id!,
        profile_url: formData.profile_url!,
        repeat_per_day: formData.repeat_per_day!,
      });
      props.onClose();
    } catch (e) {
      // Ошибка обрабатывается в store
    }
  };

  return {
    t,
    formData,
    setFormData,
    availableResources,
    detectedSource,
    handleUrlChange,
    handleSubmit,
    isLoading: isLoadingAction,
  };
};
